import {StylelessButton} from '@dropbox/dig-components/buttons';
import {Menu} from '@dropbox/dig-components/menu';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine, ChevronDownLine} from '@dropbox/dig-icons/assets';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {t} from 'i18next';
import {useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router';

import styles from './Roadmap.module.css';

const ROADMAP_VIEW_OPTIONS = [
  {id: 'product', labelKey: 'product', path: ROUTE_PATHS.ROADMAP_PRODUCT},
  {id: 'engineering', labelKey: 'engineering', path: ROUTE_PATHS.ROADMAP},
] as const;

export type RoadmapViewId = (typeof ROADMAP_VIEW_OPTIONS)[number]['id'];

const getRoadmapViewPath = (view: RoadmapViewId): string =>
  ROADMAP_VIEW_OPTIONS.find((option) => option.id === view)?.path ?? ROUTE_PATHS.ROADMAP;

interface RoadmapViewNavigationState {
  previousEngineeringHash?: string;
  previousEngineeringSearch?: string;
}

const getRoadmapViewNavigationState = (state: unknown): RoadmapViewNavigationState | undefined => {
  if (!state || typeof state !== 'object') return undefined;
  const maybeState = state as Record<string, unknown>;
  return {
    previousEngineeringHash:
      typeof maybeState.previousEngineeringHash === 'string'
        ? maybeState.previousEngineeringHash
        : undefined,
    previousEngineeringSearch:
      typeof maybeState.previousEngineeringSearch === 'string'
        ? maybeState.previousEngineeringSearch
        : undefined,
  };
};

export const buildRoadmapViewUrl = ({
  hash,
  search,
  state,
  view,
}: {
  hash: string;
  search: string;
  state?: unknown;
  view: RoadmapViewId;
}) => {
  const nextPath = getRoadmapViewPath(view);
  if (view === 'engineering') {
    const previousEngineeringLocation = getRoadmapViewNavigationState(state);
    return `${nextPath}${previousEngineeringLocation?.previousEngineeringSearch ?? search}${
      previousEngineeringLocation?.previousEngineeringHash ?? hash
    }`;
  }

  const currentSearchParams = new URLSearchParams(search);
  const nextSearchParams = new URLSearchParams();
  const timeframe = currentSearchParams.get('timeframe');
  if (timeframe) {
    nextSearchParams.set('timeframe', timeframe);
  }
  const nextSearch = nextSearchParams.toString();
  return `${nextPath}${nextSearch ? `?${nextSearch}` : ''}`;
};

export const buildRoadmapViewState = ({
  hash,
  search,
  view,
}: {
  hash: string;
  search: string;
  view: RoadmapViewId;
}): RoadmapViewNavigationState | undefined => {
  if (view !== 'product') return undefined;
  return {
    previousEngineeringHash: hash,
    previousEngineeringSearch: search,
  };
};

const RoadmapViewDropdown = ({
  selectedView,
  onSelect,
}: {
  selectedView: RoadmapViewId;
  onSelect: (view: RoadmapViewId) => void;
}) => {
  const selectedOption =
    ROADMAP_VIEW_OPTIONS.find((option) => option.id === selectedView) ?? ROADMAP_VIEW_OPTIONS[0];
  const selectedLabel = t(selectedOption.labelKey);

  return (
    <Menu.Wrapper closeOnSelection onSelection={(view: RoadmapViewId) => onSelect(view)}>
      {({getContentProps, getTriggerProps}) => (
        <>
          <StylelessButton
            {...getTriggerProps()}
            type="button"
            className={styles.roadmapViewTrigger}
            aria-label={`Roadmap view: ${selectedLabel}`}
          >
            <Box display="flex" alignItems="center" style={{gap: '4px'}}>
              <Title size={18}>{selectedLabel}</Title>
              <UIIcon src={ChevronDownLine} size="small" />
            </Box>
          </StylelessButton>
          <Menu.Content {...getContentProps()} placement="bottom-start" style={{minWidth: 180}}>
            <Menu.Segment>
              {ROADMAP_VIEW_OPTIONS.map((option) => (
                <Menu.ActionItem
                  key={option.id}
                  value={option.id}
                  withRightAccessory={
                    option.id === selectedView ? (
                      <UIIcon src={CheckmarkLine} size="small" />
                    ) : undefined
                  }
                >
                  {t(option.labelKey)}
                </Menu.ActionItem>
              ))}
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};

export const RoadmapViewTitle = ({selectedView}: {selectedView: RoadmapViewId}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const handleSelectRoadmapView = useCallback(
    (view: RoadmapViewId) => {
      const nextPath = getRoadmapViewPath(view);
      if (location.pathname.replace(/\/+$/, '') === nextPath) {
        return;
      }
      navigate(
        buildRoadmapViewUrl({
          hash: location.hash,
          search: location.search,
          state: location.state,
          view,
        }),
        {
          state: buildRoadmapViewState({
            hash: location.hash,
            search: location.search,
            view,
          }),
          viewTransition: true,
        }
      );
    },
    [location.hash, location.pathname, location.search, location.state, navigate]
  );

  return (
    <Box display="flex" alignItems="center" style={{gap: '6px'}}>
      <Title size={18}>{t('roadmap')}</Title>
      <Title size={18}>•</Title>
      <RoadmapViewDropdown selectedView={selectedView} onSelect={handleSelectRoadmapView} />
    </Box>
  );
};
